<template>
  <div
    class="so-data-table"
    :class="{
      'so-data-table--card': card,
      'so-data-table--loading': loading,
      'so-data-table--row-clickable': rowClickable,
    }"
    data-testid="so-data-table"
    :aria-busy="loading"
    v-bind="$attrs"
  >
    <v-data-table
      ref="table"
      v-model="selectedItems"
      :headers="dataTableHeaders"
      :items="dataTableItems"
      :custom-key-sort="customKeySort"
      :loading="loading"
      :show-select="selectable"
      :select-strategy="multiple ? 'page' : 'single'"
      :item-value="selectKey"
      :search="searchable ? searchQuery : undefined"
      :sort-by="sortBy"
      :items-per-page="hasPagination ? defaultItemsPerPage : -1"
      :items-per-page-options="hasPagination ? defaultItemsPerPageOptions : []"
      :class="{ 'so-data-table--no-pagination': !hasPagination }"
      :custom-filter="customFilter"
      :hover="rowClickable"
      :show-expand="expandable"
      class="rounded"
      data-testid="so-data-table-table"
      fixed-header
      :return-object="!selectKey"
      :density="compact ? 'compact' : 'default'"
      @click:row="onRowClick"
    >
      <template #top>
        <slot name="top">
          <div
            v-if="hasFilters || searchable || $slots.tableActions"
            class="d-flex justify-space-between pt-2 pb-5"
            data-testid="so-data-table-actions"
          >
            <SoDataTableFiltersDropdown
              v-if="hasFilters"
              v-model:filters="innerFilters"
              :filter-width="filterWidth"
            />
            <SoTextField
              v-if="searchable"
              v-model="searchQuery"
              :placeholder="searchPlaceholder"
              :style="{ minWidth: '240px' }"
              data-testid="so-data-table-search"
              append-icon="search"
              hide-details
              autofocus
              clearable
            />
            <slot name="tableActions"></slot>
          </div>
          <SoDataTableFiltersChips v-model:filters="innerFilters" />
        </slot>
      </template>

      <template
        #item.data-table-select="{ internalItem, isSelected, toggleSelect }"
      >
        <v-radio
          v-if="!multiple"
          :value-comparator="() => isSelected(internalItem)"
          color="success"
          tabindex="0"
          :readonly="isSelected(internalItem)"
          @click="toggleSelect(internalItem)"
        />
        <v-checkbox-btn
          v-else
          :value-comparator="() => isSelected(internalItem)"
          color="success"
          tabindex="0"
          hide-details="auto"
          @click="toggleSelect(internalItem)"
        />
      </template>

      <template
        v-for="header in headers"
        #[`header.${header.key}`]="{ column, isSorted, getSortIcon }"
        :key="`${header.key}`"
      >
        <div class="so-data-table__header">
          <v-hover v-slot="{ isHovering, props }">
            <span v-bind="props" class="d-flex align-center">
              <slot :name="`header.${header.key}`" v-bind="{ column }">
                <SoDataTableCell :value="column.title" />
              </slot>
              <v-icon
                v-if="column.sortable"
                class="so-data-table__icon"
                :class="{
                  'so-data-table__icon--hide': !isHovering && !isSorted(column),
                  [`so-data-table__icon--align-${column.align}`]: column.align,
                }"
                :icon="getSortIcon(column) as string"
                :color="!isSorted(column) ? 'default' : ''"
              ></v-icon>
            </span>
          </v-hover>
        </div>
      </template>

      <template
        v-for="column in headers"
        #[`item.${column.key}`]="{ internalItem }"
        :key="`${column.key}`"
      >
        <div
          data-test="so-data-table-cell"
          :class="{ color: internalItem.raw.rowColor }"
          :style="{
            backgroundColor:
              (internalItem.raw.rowColor as string) || 'transparent',
          }"
        >
          <slot :name="`item.${column.key}`" v-bind="{ internalItem }">
            <SoDataTableCell
              :value="internalItem.raw[column.key]"
              :type="column.type"
              :fallback-value="column.fallbackValue"
            />
          </slot>
        </div>
      </template>

      <template #item.actions="rowData">
        <slot name="itemActions" v-bind="rowData"></slot>
      </template>

      <template v-if="expandable" #expanded-row="{ item }">
        <slot name="expandableData" v-bind="item"></slot>
      </template>

      <template v-if="!hasPagination" #bottom>
        <!-- Hide pagination -->
      </template>

      <template #no-data>
        <div
          class="text-center text-grey py-16"
          data-testid="so-data-table-no-data"
        >
          <SoIcon icon="close-circle" size="x-large" />
          <p class="text-h5 mt-3">{{ noDataText }}</p>
        </div>
      </template>

      <template #footer.prepend>
        <div
          v-if="items.length && !loading"
          data-testid="so-data-table-copy"
          class="mr-auto"
        >
          <SoCopyToClipboard
            v-if="showCopyToClipboard"
            :element-to-copy="tableContents"
          />
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import dayjs from "dayjs"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import { VDataTable } from "vuetify/components/VDataTable"
  import {
    formatDate,
    formatDatePeriod,
    formatDateTime,
  } from "@/helpers/dateHelper"
  import * as sorters from "./sorters"
  import {
    SoDataTableColumnType as ColumnType,
    SoDataTableCompareFunction,
    SoDataTableFilters,
    SoDataTableHeader,
    SoDataTableItem,
    SoDataTablePaginationOption,
    SoDataTableSortItem,
  } from "@/types/components/SoDataTable"
  import { SoChipType } from "@/types/components/SoChip"
  import SoCopyToClipboard, {
    CopyToClipboardElement,
  } from "../SoCopyToClipboard.vue"
  import {
    DEFAULT_PAGINATION_OPTIONS,
    DEFAULT_PAGINATION_SIZE,
  } from "@/constants/general"
  import SoDataTableCell from "./SoDataTableCell.vue"
  import SoDataTableFiltersDropdown from "./SoDataTableFiltersDropdown.vue"
  import SoDataTableFiltersChips from "./SoDataTableFiltersChips.vue"
  import { cloneDeep } from "lodash-es"

  export default defineComponent({
    name: "SoDataTable",

    components: {
      SoTextField,
      SoIcon,
      SoCopyToClipboard,
      SoDataTableCell,
      SoDataTableFiltersDropdown,
      SoDataTableFiltersChips,
    },
    inheritAttrs: false,
    props: {
      modelValue: {
        type: Array as PropType<SoDataTableItem[] | unknown[]>,
        default: () => [],
      },
      headers: {
        type: Array as PropType<SoDataTableHeader[]>,
        default: () => [],
      },
      items: {
        type: Array as PropType<SoDataTableItem[]>,
        default: () => [],
      },
      filters: {
        type: Object as PropType<SoDataTableFilters>,
        default: undefined,
      },
      hasPagination: {
        type: Boolean,
        default: true,
      },
      defaultItemsPerPage: {
        type: Number,
        default: DEFAULT_PAGINATION_SIZE,
      },
      defaultItemsPerPageOptions: {
        type: Array as PropType<SoDataTablePaginationOption[]>,
        default: DEFAULT_PAGINATION_OPTIONS,
      },
      searchable: {
        type: Boolean,
        default: true,
      },
      selectable: {
        type: Boolean,
        default: false,
      },
      selectKey: {
        type: String,
        default: "id",
      },
      multiple: {
        type: Boolean,
        default: false,
      },
      sortBy: {
        type: Array as PropType<SoDataTableSortItem[]>,
        default: undefined,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      noDataText: {
        type: String,
        default: "No results",
      },
      actionsHeaderText: {
        type: String,
        default: "Actions",
      },
      rowClickable: {
        type: Boolean,
        default: false,
      },
      compact: {
        type: Boolean,
        default: false,
      },
      showCopyToClipboard: {
        type: Boolean,
        default: false,
      },
      searchPlaceholder: {
        type: String,
        default: "Search",
      },
      card: {
        type: Boolean,
        default: false,
      },
      filterWidth: {
        type: String,
        default: null,
      },
    },

    emits: ["update:modelValue", "click:row", "update:filters"],

    data() {
      return {
        searchQuery: "",
        selectedItems: [] as SoDataTableItem[] | unknown[],
        innerFilters: {} as SoDataTableFilters,
        ColumnType,
        tableContents: null as CopyToClipboardElement,
        formatDate,
        formatDateTime,
        formatDatePeriod,
        SoChipType,
      }
    },

    computed: {
      hasFilters() {
        return this.innerFilters && Object.keys(this.innerFilters).length
      },
      dataTableHeaders(): SoDataTableHeader[] {
        const headers: SoDataTableHeader[] = this.headers
          .filter((h) => h.isVisible !== false)
          .map((header) => ({
            key: header.key,
            title: header.title,
            colspan: header.colspan,
            rowspan: header.rowspan,
            align: header.align,
            sortable: header.sortable,
            sort: header.sort || this.getSorter(header.type),
          }))

        if (this.$slots.itemActions) {
          headers.push({
            key: "actions",
            title: this.actionsHeaderText,
            align: "end",
            sortable: false,
          })
        }

        return headers
      },
      dataTableItems(): SoDataTableItem[] {
        if (!this.hasFilters) {
          return this.items
        }

        return this.items.filter((item) => {
          return Object.keys(this.innerFilters).every((filterName) => {
            const filter = this.innerFilters[filterName]
            if (!filter.filter) return true
            return filter.filter(this.innerFilters, item)
          })
        })
      },
      customKeySort() {
        const customKeySortData = {}

        this.dataTableHeaders.forEach((header) => {
          customKeySortData[header.key] = header.sort
        })
        return customKeySortData
      },
      expandable() {
        return !!this.$slots.expandableData
      },
    },

    watch: {
      selectedItems(newVal) {
        this.$emit("update:modelValue", newVal)
      },
      modelValue(newVal) {
        this.selectedItems = newVal
      },
      innerFilters: {
        deep: true,
        handler(nevVal) {
          this.$emit("update:filters", nevVal)
        },
      },
      filters: {
        deep: true,
        handler(nevVal) {
          this.innerFilters = nevVal
        },
      },
    },

    created() {
      if (this.modelValue) {
        this.selectedItems = this.modelValue
      }
      if (this.filters) {
        this.innerFilters = cloneDeep(this.filters)
      }
    },

    mounted() {
      this.tableContents = (
        this.$refs.table as typeof VDataTable
      ).$el.querySelector("table")
    },

    methods: {
      onRowClick(event: Event, { internalItem }: unknown) {
        if (this.selectable && !this.multiple) {
          this.selectedItems = [internalItem.value]
        }
        this.$emit("click:row", internalItem.raw)
      },
      getSorter(
        columnType: ColumnType | undefined,
      ): SoDataTableCompareFunction | undefined {
        switch (columnType) {
          case ColumnType.DATE:
          case ColumnType.DATE_TIME:
            return sorters.dateSorter
          case ColumnType.DATE_PERIOD:
            return sorters.datePeriodSorter
          case ColumnType.STATUSES:
            return sorters.statusesSorter
          case ColumnType.CHIP:
            return sorters.chipSorter
          case ColumnType.STATUS:
            return sorters.statusSorter
          case ColumnType.MONEY:
          case ColumnType.MONEY_COLORED:
          case ColumnType.MONEY_ARROW:
            return sorters.moneySorter
          default:
            return undefined
        }
      },
      customFilter(value, query: string): boolean {
        if (!value || !query) return false

        query = query.trim().toLowerCase()

        if (typeof value === "string") {
          return value.toLowerCase().includes(query)
        }

        if (typeof value === "number") {
          return value.toString().includes(query)
        }

        if (typeof value === "boolean") {
          return value.toString().includes(query)
        }

        if (dayjs.isDayjs(value)) {
          return this.formatDate(value).toLowerCase().includes(query)
        }

        if ("datePeriod" in value) {
          if (!value.datePeriod?.from || !value.datePeriod?.to) return false
          return this.formatDatePeriod(
            value.datePeriod.from,
            value.datePeriod.to,
          )
            .toLowerCase()
            .includes(query)
        }

        // Custom components

        if ("value" in value && typeof value.value === "string") {
          return value.value.toLowerCase().includes(query)
        }

        if (Array.isArray(value)) {
          return value
            .reduce((text, item) => `${text} ${item.value}`, "")
            .toLowerCase()
            .includes(query)
        }

        return false
      },
    },
  })
</script>

<style>
  @import url("./SoDataTable.css");
</style>

<style scoped>
  :deep(.v-table > .v-divider:last-of-type) {
    display: none;
  }
</style>
